import { useQuery } from "@apollo/client/react";
import { useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useHasFeature } from "@/auth/access-features";
import { FEATURE_CATALOG } from "@/auth/feature-catalog";
import {
  MyselfDocument,
  SelfAssessmentSavedAnswersDocument,
  SelfAssessmentStatementsDocument,
} from "@/graphql/generated/operations";
import { elementDescription } from "@/lib/element-descriptions";
import { elementEvaluation, GROW_EXAMPLE_COUNT } from "@/lib/element-evaluations";
import { buildGrowAssistantContext, growFollowUps, growNoteLabelsItem } from "@/lib/grow-assistant-context";
import type { GrowNoteCount } from "@/lib/grow-note";
import { growNotebookDraft } from "@/lib/grow-studio-notebook";
import { questionnaireState, weakestStatements } from "@/lib/self-assessment";
import { useAssistantContext, type AssistantContextItem } from "@/lib/use-assistant-context";
import { useAgentStore } from "@/stores/agent-store";

/** What a Grow page already knows about the power on screen. The hook adds the person's own data. */
export interface GrowImproveFacts {
  /**
   * The power's key, or null for an unknown power: the queries keyed by the power are skipped and
   * nothing is built. The profile is still read: it takes no power.
   */
  element: string | null;
  powerName: string;
  /**
   * The SELF-ASSESSMENT score, never a combined one: the assistant may only speak about the
   * self-assessment (@external-adr AGENT-SERVICE-0018-self-assessment-only-power-access).
   */
  score: number | null;
  answered: number;
  total: number;
  /** genesis' own band wording, the text the page shows beside the score. */
  bandLabel: string | null;
  /**
   * The page's own query for the score and the answered count is in flight, including a refetch:
   * right after a save the cached score is the old one.
   */
  scoreLoading: boolean;
}

/**
 * The improve trigger, shared by the two Grow pages that offer it (`ElementResultPage`,
 * `PowerDetailPage`). `improve` is the click: it registers the person's data for the power on screen
 * and sends the question at once. The rules it implements are
 * @adr SPARK-0059-grow-improve-trigger-sends-on-the-first-click@1.
 */
export function useGrowImproveContext(facts: GrowImproveFacts) {
  const intl = useIntl();
  const ask = useAgentStore((s) => s.ask);
  const { element, powerName, score, answered, total, bandLabel, scoreLoading } = facts;
  // Studio as the sidebar shows it: the Save pillar and its Studio item. Two calls rather than one
  // behind `&&`, which would make the second hook conditional. Without both there is no save chip and
  // no hint pointing to one (spark#1218).
  const canSavePillar = useHasFeature(FEATURE_CATALOG.save.key);
  const canUseStudio = useHasFeature(FEATURE_CATALOG.savePins.key);
  const canSave = canSavePillar && canUseStudio;
  // The score exists from the first answer on (genesis averages whatever is there), so
  // completeness is the only thing that says whether it may be evaluated.
  const qState = questionnaireState(answered, total);

  // `element as never` because the generated variable is `CreativityElement!` and the page holds a
  // plain string; `skip` keeps an unknown power from firing a request with an invalid enum (spark#1029
  // §7, "Drei neue Queries").
  const variables = { element: element as never };
  const statements = useQuery(SelfAssessmentStatementsDocument, {
    variables,
    skip: !element,
    fetchPolicy: "cache-and-network",
  });
  const saved = useQuery(SelfAssessmentSavedAnswersDocument, {
    variables,
    skip: !element,
    fetchPolicy: "cache-and-network",
  });
  // The profile cache is warm on every Grow route (AppShell renders AccountMenu); cache-first keeps
  // a fresh mount from firing a request of its own (spark#1029 §1).
  const myself = useQuery(MyselfDocument, { fetchPolicy: "cache-first" });
  // The trigger waits until everything it would freeze is current (ADR rule 3). The score and the
  // saved answers wait out a refetch as well: they change with every save, and the result page is
  // reached straight from one, so their cached values are the previous state. The statements and the
  // profile do not change that way, so cached ones count. A failed query ends its wait like any
  // other, so an error never leaves the button dead.
  const contextPending =
    scoreLoading ||
    saved.loading ||
    (statements.loading && !statements.data) ||
    (myself.loading && !myself.data);

  const statementList = statements.data?.selfAssessmentStatements;
  const savedAnswers = saved.data?.selfAssessmentSavedAnswers;
  const weak = useMemo(() => weakestStatements(statementList ?? [], savedAnswers), [statementList, savedAnswers]);
  const profile = myself.data?.myself ?? null;
  const growInput = useMemo(
    () =>
      element
        ? {
            powerName,
            elementKey: element,
            definition: elementDescription(element, intl.locale),
            score,
            answered,
            total,
            bandLabel,
            // The banded paragraph for the SELF-ASSESSMENT score, although the power page displays the
            // one for the combined score: page context carries self-assessment values only
            // (@external-adr AGENT-SERVICE-0020-ag-ui-protocol-adoption), and the paragraph is quoted
            // verbatim after the lead-in (agent-service#124, note_44700 point 3).
            assessment: elementEvaluation(element, score, intl.locale),
            weak,
            profile,
            // The answer's fixed texts in the app's language, so the prompt carries no
            // German/English pairs and the headings follow the UI like any other string.
            labels: {
              leadIn: intl.formatMessage({ id: "grow.answer.leadIn" }),
              growthAreas: intl.formatMessage({ id: "grow.answer.growthAreas" }),
              example: intl.formatMessage({ id: "grow.answer.example" }),
              situation: intl.formatMessage({ id: "grow.answer.situation" }),
              approach: intl.formatMessage({ id: "grow.answer.approach" }),
              outcome: intl.formatMessage({ id: "grow.answer.outcome" }),
              assessmentName: intl.formatMessage({ id: "grow.answer.assessmentName" }),
              // Below the bar there are no growth areas to work on, so the hint only points to Studio.
              saveHint: canSave
                ? intl.formatMessage({
                    id: qState === "complete" ? "grow.answer.saveHint" : "grow.answer.saveHintIncomplete",
                  })
                : undefined,
            },
          }
        : null,
    [element, powerName, intl, score, answered, total, bandLabel, weak, profile, canSave, qState],
  );

  // What the click registered, for ONE power (ADR rules 2 and 4). The route reuses the page component
  // when only the power in the URL changes, so the state records which power it belongs to. Another
  // power's registration is cleared while rendering, React's pattern for state that follows a prop,
  // so no commit ever pairs one power's page with another power's data; an effect would leave one
  // render with the old list still registered. `registered &&` first: with nothing registered and no
  // element both sides of `registered?.element === element` are undefined and the comparison is true.
  const [registered, setRegistered] = useState<{ element: string; items: AssistantContextItem[] } | null>(null);
  if (registered && registered.element !== element) setRegistered(null);
  useAssistantContext(registered && registered.element === element ? registered.items : null);

  const improve = () => {
    if (!element || !growInput) return;
    // The list as it is at this click, never re-derived afterwards (ADR rule 3); the next click
    // registers it anew.
    const items = buildGrowAssistantContext(growInput);
    setRegistered({ element, items });
    // What the save chip's note request sends (ADR rule 6): these very items, and the note's own fixed
    // texts, which never ride a chat turn. The brief names the power and nothing personal, and the number
    // of examples the note holds: three from the chip, one to three from a save the person typed.
    const brief = (count: GrowNoteCount) =>
      intl.formatMessage(
        { id: qState === "complete" ? "grow.notebook.brief" : "grow.notebook.briefIncomplete" },
        { name: powerName, count },
      );
    const note = canSave
      ? {
          briefs: { 1: brief(1), 2: brief(2), 3: brief(3) },
          context: [
            ...items,
            growNoteLabelsItem({
              example: intl.formatMessage({ id: "grow.answer.example" }),
              goal: intl.formatMessage({ id: "grow.notebook.goal" }),
              hack: intl.formatMessage({ id: "grow.notebook.part.hack" }),
              howTo: intl.formatMessage({ id: "grow.notebook.part.howTo" }),
              tools: intl.formatMessage({ id: "grow.notebook.part.tools" }),
              firstStep: intl.formatMessage({ id: "grow.notebook.part.firstStep" }),
              result: intl.formatMessage({ id: "grow.notebook.part.result" }),
              whyItWorks: intl.formatMessage({ id: "grow.notebook.part.whyItWorks" }),
            }),
          ],
        }
      : null;
    ask(
      // What the person sees in their own bubble: the one sentence they would have typed. It asks how
      // to develop the power, not how to raise a score, so it holds whether the in-depth
      // self-assessment is finished, half done or not started (spark#1029, "Ein fester Prompt statt
      // zehn gewürfelter (2026-10-06)"; agent-service!110 note_44675).
      intl.formatMessage({ id: "grow.ask.improveAsk" }, { name: powerName }),
      {
        // The brief rides hidden in the person's own turn, where it outranks the section prompt, so it
        // asks only for what the GROWTH contract asks too: the examples and a closing summary, no
        // score, no definition (agent-service#133).
        hiddenContext: intl.formatMessage(
          { id: qState === "complete" ? "grow.ask.improveBrief" : "grow.ask.improveBriefIncomplete" },
          { name: powerName, examples: GROW_EXAMPLE_COUNT },
        ),
        followUps: growFollowUps(
          (id) => intl.formatMessage({ id }),
          note ? { notebook: growNotebookDraft(growInput), note } : null,
        ),
        // The improve answer is the one answer long enough to need the wide drawer (WIDE_ASSISTANT_WIDTH).
        wide: true,
      },
    );
  };

  return { contextPending, qState, improve };
}
